import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Modal from "../../Modal";
import Button from "../../Button";
import FormInput from "../../Form/FormInput";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import PeriodClosedNotice from "../PeriodClosedNotice";
import {
  requestListAccount,
  useAccountingDispatch,
  useAccountingState,
  usePeriodLock,
} from "../../../../Context";
import { formatMoney, getPeriodClosedError } from "../../../../Utils";
import { formatPeriodDate } from "../../../../Utils/PeriodUtils";
import { periodStart } from "../../../../Utils/payroll";
import FormError from "../../Form/FormError";

const DEFAULT_CASH_CODE = "110001";
const NTPN_PATTERN = /^[A-Z0-9]{16}$/;

const laterOf = (a, b) => (b && b > a ? b : a);

/**
 * PayrollPaymentModal - pays something a posted run owes in one journal
 * against the chosen cash or bank account: net pay, PPh 21, or a BPJS
 * programme. The amount is the run's own and is only shown here.
 *
 * The date may fall anywhere from the payroll month's first day: salaries are
 * often paid on the 25th, before the accrual lands on the last day. It is sent
 * the way every client dates a journal, as the start of the local day.
 * `withNtpn` adds the optional NTPN from a PPh 21 receipt.
 */
const PayrollPaymentModal = ({
  open,
  onClose,
  run,
  businessID,
  title,
  amount,
  amountLabel,
  amountNote,
  submitLabel,
  note,
  withNtpn = false,
  onSubmit,
  submitting,
  error,
}) => {
  const { t, i18n } = useTranslation();
  const accountingDispatch = useAccountingDispatch();
  const { accounts, accountLoading, allAccountLoading } = useAccountingState();
  const {
    minDate: lockMinDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(businessID, { locale: i18n.language });

  const firstDay = run ? periodStart(run.period) : null;
  const minDate = firstDay ? laterOf(firstDay, lockMinDate) : undefined;

  const [account, setAccount] = useState(null);
  const [date, setDate] = useState(null);
  const [ntpn, setNtpn] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open || !businessID) return;
    requestListAccount(accountingDispatch, { page: 1, businessID, limit: 200, loadAll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessID]);

  // A fresh form every time it opens: today, pulled forward to the first day
  // the payment may carry.
  useEffect(() => {
    if (!open || !minDate) return;
    setDate(laterOf(dayjs().startOf("day").toDate(), minDate));
    setAccount(null);
    setNtpn("");
    setSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, run?.id]);

  const cashOptions = useMemo(
    () =>
      (accounts || [])
        .filter((acc) => acc.category === "CASH_AND_BANK")
        .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
        .map((acc) => ({ value: acc.id, label: `${acc.code} - ${acc.name}`, code: acc.code })),
    [accounts],
  );

  useEffect(() => {
    if (open && !account && cashOptions.length) {
      setAccount(cashOptions.find((option) => option.code === DEFAULT_CASH_CODE) || cashOptions[0]);
    }
  }, [open, account, cashOptions]);

  if (!run) return null;

  const cleanNtpn = ntpn.replace(/\s/g, "").toUpperCase();
  const dateLocked = Boolean(date) && isDateClosed(date);
  const errors = submitted
    ? {
        account: account ? null : t("payroll.pay.errors.account"),
        date: date ? null : t("payroll.pay.errors.date"),
        ntpn: cleanNtpn && !NTPN_PATTERN.test(cleanNtpn) ? t("payroll.pay.errors.ntpn") : null,
      }
    : {};
  const serverError = error && !getPeriodClosedError(error) ? error : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (!account || !date || dateLocked || submitting) return;
    if (withNtpn && cleanNtpn && !NTPN_PATTERN.test(cleanNtpn)) return;
    const payment = {
      accountID: account.value,
      paidAt: dayjs(date).startOf("day").toISOString(),
    };
    if (withNtpn && cleanNtpn) payment.ntpn = cleanNtpn;
    await onSubmit(payment);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="payroll-payment-form"
            loading={submitting}
            disabled={dateLocked}
            className="transition-transform duration-150 ease-out active:scale-[0.97] motion-reduce:transform-none"
          >
            {submitLabel}
          </Button>
        </div>
      }
    >
      <form id="payroll-payment-form" onSubmit={handleSubmit} noValidate className="space-y-5 py-2">
        <PeriodClosedNotice error={error} onRefresh={refetchPeriod} />
        <FormError error={serverError} />

        <div>
          <p className="text-sm font-medium text-gray-500">{amountLabel}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
            {formatMoney(amount)}
          </p>
          {amountNote && <p className="mt-1 text-sm text-gray-500">{amountNote}</p>}
        </div>

        <FormSelect
          required
          label="payroll.pay.account"
          placeholder="payroll.pay.accountPlaceholder"
          options={cashOptions}
          value={account}
          onChange={setAccount}
          isLoading={accountLoading || allAccountLoading}
          error={errors.account}
        />

        <div>
          <FormDate
            required
            label="payroll.pay.date"
            selected={date}
            onChange={setDate}
            minDate={minDate}
            isDateClosed={isDateClosed}
            error={errors.date}
            hint={t("payroll.pay.dateHint", {
              date: formatPeriodDate(firstDay, i18n.language),
            })}
          />
          {isClosed && (
            <p className="mt-1.5 text-xs text-amber-700">
              {dateLocked
                ? t("accounting.period.hints.dateLocked", {
                    date: closedThroughLabel,
                  })
                : t("accounting.period.banner.closedThrough", { date: closedThroughLabel })}
            </p>
          )}
        </div>

        {withNtpn && (
          <FormInput
            label="payroll.pay.ntpn"
            value={ntpn}
            onChange={(event) => setNtpn(event.target.value)}
            onClear={() => setNtpn("")}
            hint={t("common.optional")}
            maxLength={19}
            autoComplete="off"
            spellCheck={false}
            error={errors.ntpn}
          />
        )}

        {note && <p className="text-sm text-gray-500">{note}</p>}
      </form>
    </Modal>
  );
};

export default PayrollPaymentModal;
